/**
 * validate-credentials — per-field validation for the signup wizard's
 * credentials step. Used by `<SixQuestionForm>` to render on-blur error
 * chrome below each field and gate the CREATE ACCOUNT button.
 *
 * Three rule sets:
 *   - 'full'           — self-signup (collects names + email + password + ToS)
 *   - 'password-only'  — invited-user backfill (existing auth.users row;
 *                        wizard only collects password + ToS to set the
 *                        password the magic-link session never collected)
 *   - 'none'           — backfill without credentials (no validation needed
 *                        from this module)
 *
 * Password policy matches the Supabase default (≥6 chars, no character
 * requirements) — `supabase/config.toml` does not configure a custom
 * `minimum_password_length` or `password_requirements`, so the server
 * accepts anything ≥6. The wizard mirrors the server contract verbatim;
 * tightening is a product decision tracked separately.
 */

const MIN_NAME_LENGTH = 2;
const MIN_PASSWORD_LENGTH = 6;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type CredentialsMode = 'full' | 'password-only' | 'none';

export interface CredentialsValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptedTerms: boolean;
}

export interface CredentialsErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  acceptedTerms?: string;
}

export function validateCredentials(
  v: CredentialsValues,
  mode: CredentialsMode
): CredentialsErrors {
  const errors: CredentialsErrors = {};
  if (mode === 'none') return errors;

  if (mode === 'full') {
    const fn = v.firstName.trim();
    if (fn.length === 0) errors.firstName = 'First name is required';
    else if (fn.length < MIN_NAME_LENGTH)
      errors.firstName = 'First name is too short';

    const ln = v.lastName.trim();
    if (ln.length === 0) errors.lastName = 'Last name is required';
    else if (ln.length < MIN_NAME_LENGTH)
      errors.lastName = 'Last name is too short';

    const em = v.email.trim();
    if (em.length === 0) errors.email = 'Email is required';
    else if (!EMAIL_RE.test(em)) errors.email = 'Enter a valid email address';
  }

  // Password rules apply to BOTH 'full' and 'password-only' modes.
  if (v.password.length === 0) {
    errors.password = 'Password is required';
  } else if (v.password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
  }

  if (v.confirmPassword.length === 0) {
    errors.confirmPassword = 'Re-enter your password to confirm';
  } else if (v.password !== v.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  if (!v.acceptedTerms) {
    errors.acceptedTerms = 'You must agree to the Terms of Service';
  }

  return errors;
}

export function hasErrors(errors: CredentialsErrors): boolean {
  return Object.values(errors).some((e) => e !== undefined);
}
